import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { LifeBuoy, ArrowLeft, Clock, AlertCircle, RefreshCw } from "lucide-react";
import NewTicketForm from "@/components/support/NewTicketForm";
import TicketsList from "@/components/support/TicketsList";
import { EnhancedHeader } from "@/components/ui/enhanced-header";
import PullToRefresh from "react-simple-pull-to-refresh";

export default function SupportPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<string>("new-ticket");
  const queryClient = useQueryClient();

  if (!user) {
    setLocation("/auth");
    return null;
  }

  const handleBackToProfile = () => {
    setLocation("/profile");
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const switchToNewTicket = () => {
    setActiveTab("new-ticket");
  };

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    if (activeTab === "my-tickets") {
      await queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });

    }
    return Promise.resolve();
  };

  return (
    
      <div className="container mx-auto p-4 pb-20 md:p-6 lg:p-8">
        {/* Enhanced header */}
        <EnhancedHeader
            title="Support Tickets"
            backPath="/profile"
            coloredText={true}
            rightContent={null}
          />

<PullToRefresh 
      className="min-h-screen"

      onRefresh={handleRefresh}
      pullingContent={
        <div className="text-center p-2">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
        </div>
      }
      refreshingContent={
        <div className="text-center p-4">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Refreshing tickets...</p>
        </div>
      }
    >

<div className="max-w-md mx-auto">

<Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 pt-4 bg-zinc-800 rounded-full p-1.5 my-4">
            <TabsTrigger 
              value="new-ticket" 
              className="rounded-full data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              Submit New Ticket
            </TabsTrigger>
            <TabsTrigger 
              value="my-tickets" 
              className="rounded-full data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              My Tickets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="new-ticket" className="pt-2">
            <div className="bg-zinc-900 rounded-xl overflow-hidden mb-4">
              <h3 className="text-xs uppercase text-gray-400 font-medium px-4 pt-3 pb-1">
                Submit a Support Ticket
              </h3>
              <p className="text-xs text-gray-400 px-4 pb-2">
                Please provide details about your issue and we'll respond within 24-48 hours.
              </p>
              <div className="px-5 pb-6 pt-1">
                <NewTicketForm />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="my-tickets" className="pt-2">
            <div className="space-y-4">
              <div className="bg-zinc-900 rounded-xl overflow-hidden mb-4">
                <h3 className="text-xs uppercase text-gray-400 font-medium px-4 pt-3 pb-1">
                  My Support Tickets
                </h3>
                <p className="text-xs text-gray-400 px-4 pb-2">
                  View and track the status of your support tickets.
                </p>
                <div className="px-4 pb-6 pt-1">
                  <TicketsList onCreateTicket={switchToNewTicket} />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

</div>
</PullToRefresh>

      </div>
  );
}
